import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Shield, DollarSign, Monitor, Users, Lock, ChevronDown, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fadeInUp, cardReveal } from '@/lib/motion'

type Category = '全部' | '用戶操作' | '財務' | '系統' | '安全'
type DateRange = 'today' | '7d' | '30d'

const categories: Category[] = ['全部', '用戶操作', '財務', '系統', '安全']

const categoryColors: Record<string, string> = {
  '用戶操作': 'bg-blue-500/10 text-blue-400',
  '財務': 'bg-gold/10 text-gold',
  '系統': 'bg-emerald-500/10 text-emerald-400',
  '安全': 'bg-red-500/10 text-red-400',
}

const categoryIcons: Record<string, typeof Shield> = {
  '用戶操作': Users,
  '財務': DollarSign,
  '系統': Monitor,
  '安全': Lock,
}

interface LogEntry {
  time: string
  admin: string
  action: string
  category: Category
  ip: string
  fullIp: string
  entity: string
  sessionId: string
  details: string
}

const logs: LogEntry[] = [
  {
    time: '2026-02-07 14:32:08', admin: '陳主管', action: '批准提款 #W001', category: '財務', ip: '203.12.***',
    fullIp: '203.12.88.145', entity: '用戶 #12345 / 提款 #W001', sessionId: 'sess_a8f2c1d3e4',
    details: '批准用戶 大衛王 的提款申請 #W001，金額 $2,500.00。已通過 KYC 驗證及風控審核，交易將於 24 小時內完成。',
  },
  {
    time: '2026-02-07 14:28:41', admin: '王管理', action: '停用用戶 大衛王', category: '用戶操作', ip: '118.45.***',
    fullIp: '118.45.223.67', entity: '用戶 #12345 (大衛王)', sessionId: 'sess_b7e3f2a1c5',
    details: '因多次違反使用條款，停用用戶 大衛王 帳戶。帳戶餘額 $340.00 已凍結，待進一步審查。停用原因: 疑似多帳號操作。',
  },
  {
    time: '2026-02-07 13:55:12', admin: '陳主管', action: '調整佣金上限至 5%', category: '財務', ip: '203.12.***',
    fullIp: '203.12.88.145', entity: '全域設定 / 代理佣金', sessionId: 'sess_a8f2c1d3e4',
    details: '將代理佣金上限從 3% 調整至 5%。此變更影響所有活躍代理的最大佣金費率。生效時間: 立即。',
  },
  {
    time: '2026-02-07 13:41:30', admin: '系統', action: '自動備份完成 (DB snapshot)', category: '系統', ip: '10.0.1.***',
    fullIp: '10.0.1.100', entity: 'PostgreSQL 主資料庫', sessionId: 'sys_cron_daily_001',
    details: '每日自動資料庫快照已完成。備份大小: 2.3GB，耗時 4 分 32 秒。備份已加密並上傳至 Azure Blob Storage。保留策略: 30 天。',
  },
  {
    time: '2026-02-07 12:15:55', admin: '李安全', action: '封鎖 IP 段 45.134.xxx', category: '安全', ip: '192.168.***',
    fullIp: '192.168.1.50', entity: '防火牆規則 / IP 黑名單', sessionId: 'sess_c4d5e6f7a8',
    details: '封鎖 IP 段 45.134.0.0/16，該段 IP 在過去 24 小時內發起 1,247 次異常登入嘗試。已通報資安團隊並記錄至威脅情報資料庫。',
  },
  {
    time: '2026-02-07 11:48:22', admin: '王管理', action: '重設用戶密碼 Player_881', category: '用戶操作', ip: '118.45.***',
    fullIp: '118.45.223.67', entity: '用戶 #88100 (Player_881)', sessionId: 'sess_b7e3f2a1c5',
    details: '應用戶 Player_881 要求，透過客服管道重設登入密碼。已驗證身份 (Telegram ID + 綁定手機)。密碼重設連結已發送至用戶。',
  },
  {
    time: '2026-02-07 10:30:09', admin: '系統', action: '排程維護視窗已結束', category: '系統', ip: '10.0.1.***',
    fullIp: '10.0.1.100', entity: '系統維護排程', sessionId: 'sys_maint_20260207',
    details: '排程維護視窗 (02:00-04:00 UTC) 已結束。執行項目: DB vacuum、快取清理、SSL 憑證更新。所有服務已恢復正常運作。停機時間: 0 秒 (滾動更新)。',
  },
  {
    time: '2026-02-07 09:12:44', admin: '李安全', action: '標記可疑帳戶 SUS_2201', category: '安全', ip: '192.168.***',
    fullIp: '192.168.1.50', entity: '用戶 #22010 (SUS_2201)', sessionId: 'sess_c4d5e6f7a8',
    details: '標記用戶 SUS_2201 為可疑帳戶。異常行為: 24 小時內建立 3 個帳戶，IP 來源相同且存款模式一致。已限制提款功能，等待人工審核。',
  },
]

const dateRangeLabels: Record<DateRange, { start: string; end: string; label: string }> = {
  'today': { start: '2026-02-07', end: '2026-02-07', label: '今日' },
  '7d': { start: '2026-02-01', end: '2026-02-07', label: '最近 7 天' },
  '30d': { start: '2026-01-09', end: '2026-02-07', label: '最近 30 天' },
}

export function AdminAuditScreen() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Category>('全部')
  const [expandedLog, setExpandedLog] = useState<number | null>(null)
  const [dateRange, setDateRange] = useState<DateRange>('today')
  const [showDatePicker, setShowDatePicker] = useState(false)

  const range = dateRangeLabels[dateRange]

  const filtered = logs.filter((log) => {
    const matchesFilter = filter === '全部' || log.category === filter
    const matchesSearch = search === '' ||
      log.action.toLowerCase().includes(search.toLowerCase()) ||
      log.admin.toLowerCase().includes(search.toLowerCase())
    return matchesFilter && matchesSearch
  })

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
      className="p-4 pb-8 flex flex-col gap-4"
    >
      {/* Search */}
      <motion.div variants={fadeInUp} className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="搜尋操作日誌"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/40"
        />
      </motion.div>

      {/* Date Range Filter */}
      <motion.div variants={fadeInUp} className="space-y-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-surface-elevated border border-foreground/10 text-xs"
          >
            <Calendar className="w-3.5 h-3.5 text-gold" />
            <span className="text-muted-foreground">開始日期</span>
            <span className="font-mono text-foreground">{range.start}</span>
          </button>
          <span className="text-muted-foreground text-xs">~</span>
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-surface-elevated border border-foreground/10 text-xs"
          >
            <Calendar className="w-3.5 h-3.5 text-gold" />
            <span className="text-muted-foreground">結束日期</span>
            <span className="font-mono text-foreground">{range.end}</span>
          </button>
        </div>
        <AnimatePresence>
          {showDatePicker && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="flex gap-2 p-2 rounded-xl bg-surface-elevated border border-foreground/10">
                {(['today', '7d', '30d'] as DateRange[]).map((dr) => (
                  <button
                    key={dr}
                    onClick={() => {
                      setDateRange(dr)
                      setShowDatePicker(false)
                    }}
                    className={cn(
                      'flex-1 py-2 rounded-lg text-xs font-bold transition-colors',
                      dateRange === dr ? 'bg-gold/20 text-gold' : 'text-muted-foreground'
                    )}
                  >
                    {dateRangeLabels[dr].label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Filter Chips */}
      <motion.div variants={fadeInUp} className="flex gap-2 overflow-x-auto scrollbar-hide">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors',
              filter === cat ? 'bg-gold/20 text-gold' : 'bg-surface-elevated text-muted-foreground'
            )}
          >
            {cat}
          </button>
        ))}
      </motion.div>

      {/* Log Entries */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">
          操作紀錄 ({filtered.length})
        </h3>
        {filtered.map((log, i) => {
          const Icon = categoryIcons[log.category] || Shield
          const isExpanded = expandedLog === i
          return (
            <motion.div key={i} variants={cardReveal} className="rounded-xl glass-card overflow-hidden">
              <button
                onClick={() => setExpandedLog(isExpanded ? null : i)}
                className="w-full text-left p-3.5 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', categoryColors[log.category])}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-foreground truncate">{log.action}</p>
                      <p className="text-[10px] text-muted-foreground">由 {log.admin} 操作</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={cn('px-2 py-0.5 rounded-md text-[9px] font-bold whitespace-nowrap', categoryColors[log.category])}>
                      {log.category}
                    </span>
                    <ChevronDown className={cn(
                      'w-3.5 h-3.5 text-muted-foreground transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )} />
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-foreground/10 pt-1.5">
                  <span className="text-[10px] font-mono text-muted-foreground/60">{log.time}</span>
                  <span className="text-[10px] font-mono text-muted-foreground/40">IP {log.ip}</span>
                </div>
              </button>
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3.5 pb-3.5 pt-0 border-t border-foreground/10 mx-3.5 space-y-2.5">
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        <div>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-0.5">完整 IP</p>
                          <p className="text-[11px] font-mono text-foreground">{log.fullIp}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-0.5">Session ID</p>
                          <p className="text-[11px] font-mono text-foreground truncate">{log.sessionId}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-0.5">影響對象</p>
                        <p className="text-[11px] text-foreground font-bold">{log.entity}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-0.5">操作詳情</p>
                        <p className="text-[11px] text-foreground/80 leading-relaxed">{log.details}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">無符合條件的紀錄</div>
        )}
      </motion.section>
    </motion.div>
  )
}
